export const GOOGLE_REVIEW_WRITEREVIEW_BASE =
  "https://search.google.com/local/writereview?placeid=";

export function buildGoogleReviewLink(placeId: string): string {
  return `${GOOGLE_REVIEW_WRITEREVIEW_BASE}${encodeURIComponent(placeId)}`;
}

export type ReviewLinkResult =
  | { kind: "place_id"; placeId: string; reviewUrl: string; businessName?: string, videoLink?: string }
  | { kind: "direct"; reviewUrl: string; businessName?: string, videoLink?: string };

export type ReviewLinkError = { kind: "error"; message: string };

const PLACE_ID_IN_TEXT =
  /\b(ChIJ[A-Za-z0-9_-]{10,}|Eh[A-Za-z0-9_-]{10,}|GhIJ[A-Za-z0-9_-]{10,})\b/;

export function resolveReviewLinkFromInput(
  input: string
): ReviewLinkResult | ReviewLinkError {
  const trimmed = input.trim();
  if (!trimmed) {
    return { kind: "error", message: "Please enter a business name, Maps URL, or Place ID." };
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return resolveFromUrl(trimmed);
  }

  const placeIdMatch = trimmed.match(PLACE_ID_IN_TEXT);
  if (placeIdMatch) {
    const placeId = placeIdMatch[1];
    return {
      kind: "place_id",
      placeId,
      reviewUrl: buildGoogleReviewLink(placeId),
    };
  }

  if (/^[A-Za-z0-9_-]{10,}$/.test(trimmed)) {
    return {
      kind: "place_id",
      placeId: trimmed,
      reviewUrl: buildGoogleReviewLink(trimmed),
    };
  }

  return {
    kind: "error",
    message:
      "Could not find a Google Place ID. Paste your business Google Maps URL, enter a Place ID, or use business search.",
  };
}

function resolveFromUrl(url: string): ReviewLinkResult | ReviewLinkError {
  try {
    const parsed = new URL(url);

    if (parsed.hostname === "g.page" || url.includes("/review")) {
      if (url.includes("writereview")) {
        const placeId = parsed.searchParams.get("placeid");
        if (placeId) {
          return {
            kind: "place_id",
            placeId,
            reviewUrl: buildGoogleReviewLink(placeId),
          };
        }
      }
      if (url.includes("/review") || parsed.hostname === "g.page") {
        return { kind: "direct", reviewUrl: url };
      }
    }

    if (url.includes("writereview")) {
      const placeId =
        parsed.searchParams.get("placeid") ||
        url.match(/placeid=([^&]+)/i)?.[1];
      if (placeId) {
        const decoded = decodeURIComponent(placeId);
        return {
          kind: "place_id",
          placeId: decoded,
          reviewUrl: buildGoogleReviewLink(decoded),
        };
      }
    }

    const placeIdParam =
      parsed.searchParams.get("place_id") ||
      parsed.searchParams.get("query_place_id") ||
      url.match(/[?&]place_id=([^&]+)/i)?.[1];
    if (placeIdParam) {
      const placeId = decodeURIComponent(placeIdParam);
      return {
        kind: "place_id",
        placeId,
        reviewUrl: buildGoogleReviewLink(placeId),
      };
    }

    const textMatch = url.match(PLACE_ID_IN_TEXT);
    if (textMatch) {
      const placeId = textMatch[1];
      return {
        kind: "place_id",
        placeId,
        reviewUrl: buildGoogleReviewLink(placeId),
      };
    }
  } catch {
    // fall through to error
  }

  return {
    kind: "error",
    message:
      "We could not extract a Place ID from that URL. Open your business on Google Maps, click Share, copy the link, and paste it here—or enter your Place ID directly.",
  };
}

export function buildMapsSearchUrl(query: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}
